// MCHD Room Scheduler - Authentication Hook
// Handles login state and authentication logic with React Query

import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { loginUser, logoutUser, getCurrentUser, validateToken, refreshToken as refreshTokenApi, changePassword as changePasswordApi, User, LoginRequest, PasswordChangeRequest, TokenManager, setupProactiveRefresh } from '../api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean; // New state for initial authentication check
  retryAfter: number | undefined; // Seconds to wait before retry
  isAccountLocked: boolean | undefined; // Whether account is locked
}

// Query keys for React Query
const AUTH_KEYS = {
  user: ['auth', 'user'] as const,
  token: ['auth', 'token'] as const,
} as const;

export function useAuth() {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: false,
    error: null,
    isAuthenticated: false,
    isInitializing: true, // Start with initializing true to prevent flash
    retryAfter: undefined,
    isAccountLocked: false,
  });

  // Get stored token from localStorage
  const getStoredToken = useCallback((): string | null => {
    return localStorage.getItem('auth_token');
  }, []);


  // Store token in localStorage with expiration
  const storeToken = useCallback((token: string, expiresIn?: string): void => {
    if (expiresIn) {
      TokenManager.storeAccessToken(token, expiresIn);
    } else {
      localStorage.setItem('auth_token', token);
    }
  }, []);

  // Store refresh token in localStorage
  const storeRefreshToken = useCallback((refreshToken: string): void => {
    TokenManager.storeRefreshToken(refreshToken);
  }, []);

  // Remove tokens from localStorage
  const removeToken = useCallback((): void => {
    TokenManager.clearTokens();
  }, []);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      if (data.success && data.data) {
        storeToken(data.data.token, data.data.expiresIn);
        storeRefreshToken(data.data.refreshToken);
        // Convert API user to full User type
        const fullUser: User = {
          ...data.data.user,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        setAuthState({
          user: fullUser,
          isLoading: false,
          error: null,
          isAuthenticated: true,
          isInitializing: false,
          retryAfter: undefined,
          isAccountLocked: false,
        });
        queryClient.setQueryData(AUTH_KEYS.user, fullUser);
        queryClient.setQueryData(AUTH_KEYS.token, data.data.token);
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: data.error?.message || 'Login failed',
          isInitializing: false,
        }));
      }
    },
    onError: (error: any) => {
      let errorMessage = 'Login failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = error.message as string;
      }
      
      // Handle specific error codes
      let retryAfter: number | undefined;
      let isAccountLocked = false;
      
      if (errorMessage.includes('ACCOUNT_LOCKED')) {
        errorMessage = 'Your account is temporarily locked due to multiple failed login attempts. Please wait 15 minutes before trying again, or contact your system administrator for assistance.';
        retryAfter = 900; // 15 minutes
        isAccountLocked = true;
      } else if (errorMessage.includes('RATE_LIMIT_EXCEEDED')) {
        errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
        retryAfter = 300; // 5 minutes
      } else if (errorMessage.includes('INVALID_CREDENTIALS')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      }
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isInitializing: false,
        retryAfter,
        isAccountLocked,
      }));
    },
  });

  // Refresh token mutation
  useMutation({
    mutationFn: (refreshTokenValue: string) => refreshTokenApi(refreshTokenValue),
    onSuccess: (data: any) => {
      if (data.success && data.data) {
        storeToken(data.data.token, data.data.expiresIn);
        storeRefreshToken(data.data.refreshToken);
        queryClient.setQueryData(AUTH_KEYS.token, data.data.token);
      } else {
        // Refresh failed, logout user
        removeToken();
        setAuthState({
          user: null,
          isLoading: false,
          error: 'Session expired. Please log in again.',
          isAuthenticated: false,
          isInitializing: false,
          retryAfter: undefined,
          isAccountLocked: false,
        });
        queryClient.clear();
      }
    },
    onError: () => {
      // Refresh failed, logout user
      removeToken();
      setAuthState({
        user: null,
        isLoading: false,
        error: 'Session expired. Please log in again.',
        isAuthenticated: false,
        isInitializing: false,
        retryAfter: undefined,
        isAccountLocked: false,
      });
      queryClient.clear();
    },
  });

  // Password change mutation
  const passwordChangeMutation = useMutation({
    mutationFn: changePasswordApi,
    onSuccess: (data: any) => {
      if (data.success) {
        // Password changed successfully, logout user
        removeToken();
        setAuthState({
          user: null,
          isLoading: false,
          error: null,
          isAuthenticated: false,
          isInitializing: false,
          retryAfter: undefined,
          isAccountLocked: false,
        });
        queryClient.clear();
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: data.error?.message || 'Password change failed',
        }));
      }
    },
    onError: (error: any) => {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Password change failed',
        isInitializing: false,
      }));
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      removeToken();
      setAuthState({
        user: null,
        isLoading: false,
        error: null,
        isAuthenticated: false,
        isInitializing: false,
        retryAfter: undefined,
        isAccountLocked: false,
      });
      queryClient.clear();
    },
    onError: () => {
      // Even if logout fails on server, clear local state
      removeToken();
      setAuthState({
        user: null,
        isLoading: false,
        error: null,
        isAuthenticated: false,
        isInitializing: false,
        retryAfter: undefined,
        isAccountLocked: false,
      });
      queryClient.clear();
    },
  });

  // Get current user query
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: AUTH_KEYS.user,
    queryFn: async () => {
      const response = await getCurrentUser();
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get user');
      }
      return response.data;
    },
    enabled: authState.isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Login function
  const login = useCallback(async (formData: LoginRequest): Promise<void> => {
    // Prevent multiple simultaneous login attempts
    if (authState.isLoading) {
      console.warn('Login already in progress, ignoring duplicate request');
      return;
    }

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    // Validate email domain
    if (!formData.email.endsWith('@moh.gov.sa')) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Please use your organization email address',
      }));
      return;
    }

    loginMutation.mutate(formData);
  }, [loginMutation, authState.isLoading]);

  // Logout function
  const logout = useCallback((): void => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    logoutMutation.mutate();
  }, [logoutMutation]);

  // Password change function
  const changePassword = useCallback(async (passwordData: PasswordChangeRequest): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    passwordChangeMutation.mutate(passwordData);
  }, [passwordChangeMutation]);

  // Password change modal state
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);

  // Open password change modal
  const openPasswordChangeModal = useCallback((): void => {
    setIsPasswordChangeModalOpen(true);
  }, []);

  // Close password change modal
  const closePasswordChangeModal = useCallback((): void => {
    setIsPasswordChangeModalOpen(false);
  }, []);

  // Clear error function
  const clearError = useCallback((): void => {
    setAuthState(prev => ({ 
      ...prev, 
      error: null, 
      retryAfter: undefined, 
      isAccountLocked: false 
    }));
  }, []);

  // Countdown timer for retry
  useEffect(() => {
    if (authState.retryAfter && authState.retryAfter > 0) {
      const timer = setInterval(() => {
        setAuthState(prev => {
          if (prev.retryAfter && prev.retryAfter > 0) {
            return { ...prev, retryAfter: prev.retryAfter - 1 };
          }
          return prev;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
    return undefined;
  }, [authState.retryAfter]);

  // Automatic token refresh function
  const attemptTokenRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const newToken = await TokenManager.refreshAccessToken();
      if (newToken) {
        queryClient.setQueryData(AUTH_KEYS.token, newToken);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    return false;
  }, [queryClient]);

  // Check if user is authenticated on mount
  useEffect(() => {
    let isInitializing = true;
    let isInitialized = false;
    
    const initializeAuth = async () => {
      // Prevent multiple simultaneous initialization attempts
      if (!isInitializing || isInitialized) return;
      
      try {
        const token = getStoredToken();
        
        if (token) {
          // Validate token
          const response = await validateToken();
          if (response.success) {
            setAuthState(prev => ({
              ...prev,
              isAuthenticated: true,
              isInitializing: false,
            }));
            return;
          }
          
          // Token invalid, try to refresh
          const refreshed = await attemptTokenRefresh();
          if (refreshed) {
            setAuthState(prev => ({
              ...prev,
              isAuthenticated: true,
              isInitializing: false,
            }));
          } else {
            removeToken();
            setAuthState(prev => ({
              ...prev,
              isInitializing: false,
            }));
          }
        } else {
          // No token, not authenticated
          setAuthState(prev => ({
            ...prev,
            isInitializing: false,
          }));
        }
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        // Token validation failed, try to refresh
        const refreshed = await attemptTokenRefresh();
        if (!refreshed) {
          removeToken();
        }
        setAuthState(prev => ({
          ...prev,
          isInitializing: false,
        }));
      } finally {
        isInitializing = false;
        isInitialized = true;
      }
    };

    initializeAuth();
  }, []); // Empty dependency array - only run once on mount

  // Setup proactive token refresh
  useEffect(() => {
    if (authState.isAuthenticated) {
      setupProactiveRefresh();
    }
  }, [authState.isAuthenticated]);

  // Update user data when query data changes
  useEffect(() => {
    if (userData) {
      setAuthState(prev => ({
        ...prev,
        user: userData,
        isAuthenticated: true,
      }));
    }
  }, [userData]);

  return {
    user: authState.user,
    isLoading: authState.isLoading || isLoadingUser,
    error: authState.error,
    isAuthenticated: authState.isAuthenticated,
    isInitializing: authState.isInitializing,
    retryAfter: authState.retryAfter,
    isAccountLocked: authState.isAccountLocked,
    login,
    logout,
    changePassword,
    clearError,
    isPasswordChangeModalOpen,
    openPasswordChangeModal,
    closePasswordChangeModal,
    getStoredToken,
  };
}
